import { useEffect, useState } from "react";
import { ClipboardCheck, Plus, Pencil, Trash2, Check, RotateCcw, User, School } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateLocale } from "@/lib/i18n";
import ConfirmDialog from "@/components/ConfirmDialog";
import { isMediaResource, openTeachingResource, ResourcePlayerDialog } from "@/pages/enseignement/teachUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const todayStr = () => new Date().toISOString().slice(0, 10);

// « Travail à faire » (Phase 4) : l'enseignant crée/suit, l'élève consulte et marque terminé (§20-§21)
export default function AssignmentsPage() {
  const { tspace, lang, t } = useAuth();
  const isTeacher = tspace?.my_role === "teacher";
  const [assignments, setAssignments] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [resources, setResources] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delA, setDelA] = useState(null);
  const [player, setPlayer] = useState(null);
  const emptyForm = { type: "individual", student_relation_id: "", class_id: "", title: "", description: "", due_date: "", resource_ids: [] };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/teaching/assignments").then((r) => setAssignments(r.data.assignments)).catch(() => {});
    if (isTeacher) {
      api.get("/teaching/students").then((r) => setStudents(r.data.students.filter((s) => s.status === "ACTIVE"))).catch(() => {});
      api.get("/teaching/classes").then((r) => setClasses(r.data.classes)).catch(() => {});
      api.get("/teaching/resources").then((r) => setResources(r.data.resources)).catch(() => {});
    }
  };
  useEffect(() => { load(); }, [isTeacher]);

  const openRes = (r) => (isMediaResource(r) ? setPlayer(r) : openTeachingResource(r));

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      type: a.target_kind,
      student_relation_id: a.student_relation_id || "",
      class_id: a.class_id || "",
      title: a.title,
      description: a.description || "",
      due_date: a.due_date || "",
      resource_ids: (a.resources || []).map((r) => r.id),
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        due_date: form.due_date || null,
        resource_ids: form.resource_ids,
        student_relation_id: form.type === "individual" ? form.student_relation_id : null,
        class_id: form.type === "class" ? form.class_id : null,
      };
      if (editing) await api.put(`/teaching/assignments/${editing.id}`, payload);
      else await api.post("/teaching/assignments", payload);
      toast.success(t(editing ? "teach.saved" : "teach.assignment_created"));
      setOpen(false);
      load();
    } catch (e) { toast.error(e.friendly); }
    setSaving(false);
  };

  const toggleDone = async (a) => {
    try {
      await api.post(`/teaching/assignments/${a.id}/done`);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/teaching/assignments/${delA.id}`);
      setDelA(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const dueLabel = (a) => a.due_date
    ? new Date(`${a.due_date}T00:00`).toLocaleDateString(dateLocale(lang), { day: "numeric", month: "short" })
    : t("teach.no_due");
  const isOverdue = (a, done) => a.due_date && a.due_date < todayStr() && !done;
  const isDone = (a) => (isTeacher ? a.total_count > 0 && a.done_count === a.total_count : a.my_done);

  const toggleRes = (rid) => setForm((f) => ({
    ...f,
    resource_ids: f.resource_ids.includes(rid) ? f.resource_ids.filter((x) => x !== rid) : [...f.resource_ids, rid],
  }));

  const card = (a) => {
    const done = isDone(a);
    return (
      <div key={a.id} className="app-card p-4 space-y-2" data-testid={`assignment-card-${a.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">
              {a.target_kind === "individual" ? <User className="h-3.5 w-3.5 accent shrink-0" /> : <School className="h-3.5 w-3.5 brand shrink-0" />}
              <span className="truncate">{a.title}</span>
            </p>
            <p className="text-xs t-secondary mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{a.target_name}</span>
              <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${isOverdue(a, done) ? "border-rose-500/40 text-rose-400" : "border-app t-secondary"}`}>
                {isOverdue(a, done) ? `${t("teach.overdue")} · ` : ""}{dueLabel(a)}
              </span>
            </p>
          </div>
          {isTeacher && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${done ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`} data-testid={`asg-status-${a.id}`}>
              {t("teach.done_count", { done: a.done_count, total: a.total_count })}
            </span>
          )}
        </div>
        {a.description && <p className="text-xs t-secondary whitespace-pre-wrap">{a.description}</p>}
        {(a.resources || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {a.resources.map((r) => (
              <button key={r.id} onClick={() => openRes(r)} className="text-xs px-2 py-1 rounded-lg border border-app t-secondary hover:bg-white/5" data-testid={`asg-res-${a.id}-${r.id}`}>
                {r.title}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5 pt-1">
          {!isTeacher && (
            <Button size="sm" variant={done ? "outline" : "default"} className={done ? "border-app h-8 text-xs" : "brand-bg text-white h-8 text-xs"} onClick={() => toggleDone(a)} data-testid={`asg-done-${a.id}`}>
              {done ? <><RotateCcw className="h-3.5 w-3.5 mr-1" /> {t("teach.mark_todo")}</> : <><Check className="h-3.5 w-3.5 mr-1" /> {t("teach.mark_done")}</>}
            </Button>
          )}
          {isTeacher && (
            <>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 ml-auto" onClick={() => openEdit(a)} data-testid={`asg-edit-${a.id}`} aria-label={t("teach.edit_assignment")}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-400" onClick={() => setDelA(a)} data-testid={`asg-delete-${a.id}`} aria-label={t("teach.delete_assignment")}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const todo = assignments.filter((a) => !isDone(a));
  const done = assignments.filter((a) => isDone(a));
  const formValid = form.title.trim() && (form.type === "individual" ? form.student_relation_id : form.class_id);

  return (
    <div className="space-y-4 max-w-3xl" data-testid="teach-assignments-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
          <ClipboardCheck className="h-6 w-6 brand" /> {t("teach.assignments_title")}
        </h2>
        {isTeacher && (
          <Button className="brand-bg text-white" onClick={openCreate} data-testid="add-assignment-button">
            <Plus className="h-4 w-4 mr-1" /> {t("teach.add_assignment")}
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider t-secondary">{t("teach.todo_section")}</p>
        {todo.length === 0 ? (
          <p className="text-sm t-secondary py-4 text-center" data-testid="assignments-empty">
            {isTeacher ? t("teach.no_assignments") : t("teach.no_assignments_student")}
          </p>
        ) : (
          todo.map(card)
        )}
      </div>

      {done.length > 0 && (
        <div className="space-y-2 opacity-75">
          <p className="text-xs uppercase tracking-wider t-secondary">{t("teach.done_section")}</p>
          {done.map(card)}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-bg border-app" data-testid="assignment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t(editing ? "teach.edit_assignment" : "teach.add_assignment")}</DialogTitle>
            <DialogDescription className="t-secondary">{t("teach.assignment_dialog_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <p className="text-xs uppercase tracking-wider t-secondary mb-1.5">{t("teach.assignment_target")}</p>
              <div className="flex gap-2" data-testid="asg-type-chips">
                {[["individual", "teach.for_student"], ["class", "teach.for_class"]].map(([v, l]) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setForm({ ...form, type: v })}
                    disabled={!!editing}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${form.type === v ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
                    data-testid={`asg-type-${v}`}
                  >
                    {t(l)}
                  </button>
                ))}
              </div>
            </div>
            {form.type === "individual" ? (
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.lesson_student")}</Label>
                <Select value={form.student_relation_id} onValueChange={(v) => setForm({ ...form, student_relation_id: v })} disabled={!!editing}>
                  <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="asg-student-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.lesson_class_label")}</Label>
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })} disabled={!!editing}>
                  <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="asg-class-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.resource_title")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="asg-title-input" autoFocus />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.description")}</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1 bg-transparent border-app" data-testid="asg-desc-input" />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.due_date")} ({t("teach.no_due").toLowerCase()})</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="asg-due-input" />
            </div>
            {resources.length > 0 && (
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.linked_resources")}</Label>
                <div className="mt-1 space-y-1.5 max-h-40 overflow-y-auto" data-testid="asg-resources-list">
                  {resources.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" className="accent-emerald-500" checked={form.resource_ids.includes(r.id)} onChange={() => toggleRes(r.id)} data-testid={`asg-res-check-${r.id}`} />
                      {r.title}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button className="w-full brand-bg text-white" onClick={save} disabled={!formValid || saving} data-testid="asg-save-confirm">
              {t(editing ? "common.save" : "common.create")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delA}
        title={t("teach.delete_assignment")}
        description={t("teach.delete_assignment_desc")}
        onConfirm={doDelete}
        onClose={() => setDelA(null)}
        testid="assignment-delete-confirm"
      />
      {player && <ResourcePlayerDialog resource={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}
